import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import {
  Badge, Card, Select, SelectItem, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import { fetchParts, fetchPartFilters } from '../api/drilldown';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import TableSkeleton from '../components/TableSkeleton';
import { currentUser } from '../api/auth';
import { useStoreFilter } from '../lib/storeFilter';
import { usePagination } from '../lib/usePagination';
import Pagination from '../components/Pagination';
import { t } from '../lib/i18n';
import type { PartFilters, PartsRow, User } from '../api/types';
import Header from '../components/Header';

export default function Parts() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  return user ? <PartsInner user={user} /> : <Navigate to="/login" replace />;
}

function PartsInner({ user }: { user: User }) {
  const [params, setParams] = useSearchParams();
  // q = search; kind = all|low; line/pcode/class = exact-match filter codes.
  // All are kept in the URL so a deep-link from the Overview "Top parts" table
  // lands on a pre-filtered Parts page.
  const search = params.get('q') ?? '';
  const kind = (params.get('kind') ?? 'all') as 'all' | 'low';
  const line = params.get('line') ?? '';
  const pcode = params.get('pcode') ?? '';
  const cls = params.get('class') ?? '';
  const code = params.get('code') ?? '';

  const [debounced, setDebounced] = useState(search);
  const [data, setData] = useState<PartsRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Filter option lists — fetched once on mount (or when the store changes).
  const [filterOpts, setFilterOpts] = useState<PartFilters>({ lines: [], pcodes: [], classes: [], codes: [] });

  // Debounce the input so we don't fire one query per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(id);
  }, [search]);

  // Shared header store (lib/storeFilter); staff stay locked to their store.
  const [storeFilter] = useStoreFilter(user);

  // Load the Línea / Pcode / Clase option lists once on mount and whenever the
  // store filter changes. Tolerate failure (API not yet deployed, daemon down)
  // — the page still works with just search.
  useEffect(() => {
    let alive = true;
    fetchPartFilters({ sid: storeFilter ?? undefined })
      .then((opts) => { if (alive) setFilterOpts(opts); })
      .catch(() => { /* graceful degradation — leave empty option lists */ });
    return () => { alive = false; };
  }, [storeFilter]);

  const load = () => {
    let alive = true;
    // Don't query the daemon for "all parts with empty search AND no other
    // filter" — that's tens of thousands of rows. Wait for the user to type,
    // pick the low filter, or select a Línea/Pcode/Clase.
    if (debounced === '' && kind === 'all' && line === '' && pcode === '' && cls === '' && code === '') {
      setData(null);
      setError(null);
      return () => { alive = false; };
    }
    setLoading(true);
    setError(null);
    fetchParts({
      search: debounced,
      kind,
      limit: 200,
      sid: storeFilter ?? undefined,
      line: line || undefined,
      pcode: pcode || undefined,
      class: cls || undefined,
      code: code || undefined,
    })
      .then((rows) => { if (alive) setData(rows); })
      .catch((e: Error) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  };
  useEffect(() => load(), [debounced, kind, line, pcode, cls, code, storeFilter]);

  const pg = usePagination(data);

  // Recent searches — persisted per browser so a power user (e.g. a
  // counterman who keeps looking up the same 5 fast-movers) can re-search
  // with one click. localStorage only — never leaves the browser.
  const RECENT_KEY = 'paceBI:partsRecent';
  const RECENT_MAX = 8;
  const [recent, setRecent] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      return raw ? (JSON.parse(raw) as string[]).slice(0, RECENT_MAX) : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    if (!debounced || debounced.length < 2) return;
    setRecent((prev) => {
      const next = [debounced, ...prev.filter((x) => x !== debounced)].slice(0, RECENT_MAX);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* quota / private mode */ }
      return next;
    });
  }, [debounced]);

  function setSearch(v: string) {
    const next = new URLSearchParams(params);
    if (v) next.set('q', v); else next.delete('q');
    setParams(next, { replace: true });
  }
  function setKind(v: 'all' | 'low') {
    const next = new URLSearchParams(params);
    next.set('kind', v);
    setParams(next, { replace: true });
  }
  function setLine(v: string) {
    const next = new URLSearchParams(params);
    if (v) next.set('line', v); else next.delete('line');
    setParams(next, { replace: true });
  }
  function setPcode(v: string) {
    const next = new URLSearchParams(params);
    if (v) next.set('pcode', v); else next.delete('pcode');
    setParams(next, { replace: true });
  }
  function setCode(v: string) {
    const next = new URLSearchParams(params);
    if (v) next.set('code', v); else next.delete('code');
    setParams(next, { replace: true });
  }
  function setCls(v: string) {
    const next = new URLSearchParams(params);
    if (v) next.set('class', v); else next.delete('class');
    setParams(next, { replace: true });
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} storeFilter />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        <Title>{t.parts.title}</Title>
        <Text className="text-tremor-content text-sm">{t.parts.subtitle}</Text>

        <Card className="flex flex-wrap gap-4 items-end">
          <label className="text-sm flex-1 min-w-64">
            <div className="text-tremor-content text-xs mb-1">{t.parts.searchPlaceholder}</div>
            <div className="relative">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t.parts.searchPlaceholder}
                className="w-full rounded border border-tremor-border px-3 py-2 pr-9"
                autoFocus
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute inset-y-0 right-0 px-3 text-tremor-content hover:text-tremor-content-strong text-sm"
                  aria-label="Limpiar"
                  tabIndex={-1}
                >×</button>
              )}
            </div>
          </label>

          {/* kind: all | bajo mínimo */}
          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">{t.parts.kindFilter}</div>
            <Select value={kind} onValueChange={(v) => setKind(v as 'all' | 'low')} className="w-48">
              <SelectItem value="all">{t.parts.kindAll}</SelectItem>
              <SelectItem value="low">{t.parts.kindLow}</SelectItem>
            </Select>
          </label>

          {/* Línea filter */}
          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">{t.parts.lineFilter}</div>
            <Select value={line} onValueChange={setLine} className="w-52">
              <SelectItem value="">{t.parts.allLines}</SelectItem>
              {filterOpts.lines.map((o) => (
                <SelectItem key={o.code} value={o.code}>
                  {o.desc ? `${o.code} — ${o.desc}` : o.code}
                </SelectItem>
              ))}
            </Select>
          </label>

          {/* Pcode filter */}
          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">{t.parts.pcodeFilter}</div>
            <Select value={pcode} onValueChange={setPcode} className="w-52">
              <SelectItem value="">{t.parts.allPcodes}</SelectItem>
              {filterOpts.pcodes.map((o) => (
                <SelectItem key={o.code} value={o.code}>
                  {o.desc ? `${o.code} — ${o.desc}` : o.code}
                </SelectItem>
              ))}
            </Select>
          </label>

          {/* Clase filter */}
          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">{t.parts.classFilter}</div>
            <Select value={cls} onValueChange={setCls} className="w-36">
              <SelectItem value="">{t.parts.allClasses}</SelectItem>
              {filterOpts.classes.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </Select>
          </label>

          {/* Código filter (partf.codes[]) — only rendered once any codes exist
              in the catalog, so it stays out of the way until populated. v2.57. */}
          {(filterOpts.codes?.length ?? 0) > 0 && (
            <label className="text-sm">
              <div className="text-tremor-content text-xs mb-1">{t.parts.codeFilter}</div>
              <Select value={code} onValueChange={setCode} className="w-44">
                <SelectItem value="">{t.parts.allCodes}</SelectItem>
                {filterOpts.codes.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </Select>
            </label>
          )}

          {data && (
            <Text className="ml-auto">{data.length} {t.common.results}</Text>
          )}
        </Card>

        {/* Recent searches — only relevant when input is empty AND there
            are entries to show, otherwise the chip strip is just noise. */}
        {!search && recent.length > 0 && (
          <Card>
            <Text className="text-tremor-content text-xs mb-2">Búsquedas recientes</Text>
            <div className="flex flex-wrap gap-1.5">
              {recent.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setSearch(q)}
                  className="text-xs px-2 py-1 rounded-tremor-small bg-tremor-background-muted hover:bg-tremor-brand-faint text-tremor-content-strong"
                >
                  {q}
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  setRecent([]);
                  try { localStorage.removeItem(RECENT_KEY); } catch { /* */ }
                }}
                className="text-xs px-2 py-1 text-tremor-content underline hover:text-tremor-content-strong ml-auto"
              >
                Borrar historial
              </button>
            </div>
          </Card>
        )}

        {error && <ErrorRetry message={error} onRetry={load} />}
        {loading && !data && <TableSkeleton rows={6} cols={8} />}
        {data === null && !loading && !error && (
          <EmptyState message={t.parts.typeToSearch} />
        )}
        {data && data.length === 0 && !loading && <EmptyState message={t.parts.empty} />}
        {data && data.length > 0 && (
          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.parts.cols.line}</TableHeaderCell>
                  <TableHeaderCell>{t.parts.cols.part}</TableHeaderCell>
                  <TableHeaderCell>{t.parts.cols.description}</TableHeaderCell>
                  <TableHeaderCell>{t.parts.cols.pcode}</TableHeaderCell>
                  <TableHeaderCell>{t.parts.cols.class}</TableHeaderCell>
                  <TableHeaderCell>{t.parts.cols.store}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.parts.cols.onHand}</TableHeaderCell>
                  <TableHeaderCell className="text-right">{t.parts.cols.min}</TableHeaderCell>
                  <TableHeaderCell>{t.parts.cols.kind}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pg.pageRows.map((row) => (
                  <TableRow key={`${row.line}-${row.part}-${row.sid}`}>
                    <TableCell className="font-mono text-tremor-content">{row.line}</TableCell>
                    <TableCell className="font-mono">
                      <Link to={`/parts/${encodeURIComponent(row.line)}/${encodeURIComponent(row.part)}`} className="text-tremor-brand hover:underline">
                        {row.part}
                      </Link>
                    </TableCell>
                    <TableCell>{row.pdesc}</TableCell>
                    <TableCell className="font-mono text-tremor-content">{row.pcode}</TableCell>
                    <TableCell className="font-mono text-tremor-content">{row.class}</TableCell>
                    <TableCell>{row.sid}</TableCell>
                    <TableCell className="text-right">{row.on_hand}</TableCell>
                    <TableCell className="text-right">{row.min}</TableCell>
                    <TableCell>
                      {row.kind === 'low' ? (
                        <Badge color="red">{t.parts.kindBadge.low}</Badge>
                      ) : (
                        <Badge color="green">{t.parts.kindBadge.normal}</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination {...pg} className="px-4" />
          </Card>
        )}
      </main>
    </div>
  );
}
